<template>
    <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card>
            <v-card-title class="text-h6 font-weight-bold white--text" style="background-color: #117443;">
                {{ $t('esgPlatform.validation.viewQuestionResponse') }}
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <div v-if="questionData">
                    <!-- Farm Section -->
                    <div v-if="answerData && answerData.farmName && isFarmerSeparateResponse(questionData.respondentType)" class="mb-4">
                        <div class="text-h6 font-weight-bold mb-2">
                            {{ $t('esgPlatform.validation.farm') }}
                        </div>
                        <v-chip color="primary" class="mb-1">
                            {{ answerData.farmName }}
                        </v-chip>
                    </div>
                    <!-- Answer Section -->
                    <div>
                        <div class="text-body-1">
                             <!-- Dimitra Module -->
                            <template v-if="isDimitraModule">
                                <div v-for="(moduleData, moduleName) in dimitraModuleData" :key="moduleName" class="mb-4">
                                    <div class="text-subtitle-1 font-weight-bold mb-2" style="color: #117443;">{{ getDimitraModuleDisplayName(moduleName) }}</div>
                                    <v-card v-for="(row, idx) in moduleData.rows" :key="idx" outlined class="mb-3 pa-3" style="background-color: #f9f9f9; border-color: #e0e0e0;">
                                        <v-row dense>
                                            <v-col cols="12" sm="6" md="4" v-for="(val, key) in filterRowData(row)" :key="key">
                                                <div class="text-caption grey--text text--darken-1 text-capitalize">{{ getTranslatedDimitraFieldLabel(moduleName, key) }}</div>
                                                <div class="text-body-2 font-weight-medium">
                                                    <template v-if="isFarmLocationField(moduleName, key) && getFarmLocationCoordinateFromValue(val)">
                                                        <button
                                                            type="button"
                                                            class="map-link-button"
                                                            @click="openFarmLocationDialogWithTitle(row.farmName || getQuestionTitle(), val)"
                                                        >
                                                            {{ $t('esgPlatform.validation.showOnMap') }}
                                                        </button>
                                                    </template>
                                                    <template v-else-if="isFarmGeofenceField(moduleName, key) && getFarmGeofenceCoordinates(val).length >= 3">
                                                        <button
                                                            type="button"
                                                            class="map-link-button"
                                                            @click="openFarmGeofenceDialogWithTitle(row.farmName || getQuestionTitle(), val)"
                                                        >
                                                            {{ $t('esgPlatform.validation.showOnMap') }}
                                                        </button>
                                                    </template>
                                                    <template v-else>
                                                        {{ formatDimitraFieldValue(val) }}
                                                    </template>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </div>
                                <span v-if="!Object.keys(dimitraModuleData).length">{{ $t('esgPlatform.validation.noModuleDataAvailable') }}</span>
                            </template>

                            <template v-else-if="isChoiceType && answerData.type === 'getData'">
                                <div v-if="answerData.response">
                                    <div class="text-caption grey--text mb-1">
                                        {{ answerData.response.title }}
                                    </div>
                                    <span v-html="formatAnswer"></span>
                                </div>
                                <span v-else>{{ $t('esgPlatform.validation.noData') }}</span>
                            </template>

                             <!-- Radio / Checkbox: question + options -->
                            <template v-else-if="isChoiceType && allOptions.length > 0">
                                <div class="question-card">
                                    <!-- Question Title -->
                                    <div class="question-title mb-4">
                                        <span class="font-weight-bold">{{ getQuestionIndex() }}</span>
                                        <span class="font-weight-bold ml-1">{{ getQuestionTitle() }}</span>
                                    </div>
                                    <div class="text-h6 font-weight-bold mb-2">{{ $t('esgPlatform.validation.answer') }}</div>

                                    <!-- Options -->
                                    <div
                                        v-for="(option, idx) in allOptions"
                                        :key="idx"
                                        class="option-row d-flex align-start"
                                    >
                                        <v-icon
                                            small
                                            class="mr-2 mt-1"
                                            :color="isOptionSelected(option) ? '#117443' : '#9E9E9E'"
                                        >
                                            {{ getChoiceIcon(isOptionSelected(option)) }}
                                        </v-icon>
                                        <span :class="isOptionSelected(option) ? 'option-selected' : 'option-unselected'">
                                            {{ option.label }}
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <template v-else-if="isChoiceType">
                                <div class="question-card">
                                    <div class="question-title mb-4">
                                        <span class="font-weight-bold">{{ getQuestionIndex() }}</span>
                                        <span class="font-weight-bold ml-1">{{ getQuestionTitle() }}</span>
                                    </div>
                                    <div v-if="selectedOptions.length">
                                        <div
                                            v-for="(option, idx) in selectedOptions"
                                            :key="idx"
                                            class="option-row d-flex align-start"
                                        >
                                            <v-icon small class="mr-2 mt-1" color="#117443">{{ getChoiceIcon(true) }}</v-icon>
                                            <span class="option-selected">{{ option.label }}</span>
                                        </div>
                                    </div>
                                    <span v-else>{{ $t('esgPlatform.validation.noSelection') }}</span>
                                </div>
                            </template>

                            <template v-else>
                                <div class="mb-4">
                                    <div class="text-h6 font-weight-bold mb-2">
                                        {{ getQuestionIndex() }} {{ getQuestionTitle() }}
                                    </div>
                                    <div v-if="getQuestionDescription()" class="text-body-1">
                                        {{ getQuestionDescription() }}
                                    </div>
                                </div>
                                <div class="text-h6 font-weight-bold mb-2">{{ $t('esgPlatform.validation.answer') }}</div>
                                <span v-html="formatAnswer"></span>
                            </template>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions class="pa-6 justify-end">
                <v-btn color="primary" @click="closeDialog">
                    {{ $t('esgPlatform.close') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog
            v-model="farmLocationDialog"
            max-width="980"
            scrollable
        >
            <v-card>
                <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dialog-header">
                    <div class="pr-4">
                        <div class="text-caption font-weight-bold mb-2 text-uppercase" style="color: #117443;">
                            {{ $t('esgPlatform.validation.farmLocation') }}
                        </div>
                        <div class="text-h6 font-weight-bold">
                            {{ farmLocationDetails.title || $t('esgPlatform.validation.farmLocation') }}
                        </div>
                    </div>
                    <v-btn icon @click="closeFarmLocationDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>

                <v-card-text class="px-6 pt-5 pb-2">
                    <div ref="farmLocationMap" class="farm-map"></div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6 pt-2">
                    <v-spacer />
                    <v-btn outlined color="primary" @click="closeFarmLocationDialog">
                        {{ $t('esgPlatform.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="farmGeofenceDialog"
            max-width="980"
            scrollable
        >
            <v-card>
                <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dialog-header">
                    <div class="pr-4">
                        <div class="text-caption font-weight-bold mb-2 text-uppercase" style="color: #117443;">
                            {{ $t('esgPlatform.validation.farmGeofence') }}
                        </div>
                        <div class="text-h6 font-weight-bold">
                            {{ farmGeofenceDetails.title || $t('esgPlatform.validation.farmGeofence') }}
                        </div>
                    </div>
                    <v-btn icon @click="closeFarmGeofenceDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>

                <v-card-text class="px-6 pt-5 pb-2">
                    <div ref="farmGeofenceMap" class="farm-map"></div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6 pt-2">
                    <v-spacer />
                    <v-btn outlined color="primary" @click="closeFarmGeofenceDialog">
                        {{ $t('esgPlatform.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';
export default {
    name: 'QuestionDialog',
    props: {
        dialog: {
            type: Boolean,
            default: false
        },
        questionData: {
            type: Object,
            default: null
        },
        answerData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            contextPanel: [], 
            farmGeofenceDialog: false,
            farmGeofenceDetails: {
                title: '',
                coordinates: [],
            },
            farmGeofenceMap: null,
            farmGeofencePolygon: null,
            farmLocationDialog: false,
            farmLocationDetails: {
                title: '',
                coordinate: null,
            },
            farmLocationMap: null,
            farmLocationMarker: null,
        };
    },
    computed: {
        hasContextData() {
            return this.questionData && (
                this.questionData.issue ||
                this.questionData.goal ||
                this.questionData.assessment ||
                this.questionData.heading
            );
        },
        allOptions() {
            return this.questionData?.question?.options || this.questionData?.options || [];
        },
        isDimitraModule() {
            return this.answerData?.type === 'dimitraModule' || this.answerData?.type === 'dimitraModuleFromOption' || this.getQuestionType() === 'dimitra-module';
        },
        dimitraModuleData() {
            return this.answerData?.response?.dimitraModule || {};
        },
        isChoiceType() {
            const type = this.getQuestionType();
            return ['radio-button', 'check-boxes'].includes(type);
        },
        selectedOptions() {
            const val = this.answerData?.response?.value;
            if (!val) return [];
            if (Array.isArray(val)) return val.filter(v => v && v.label);
            if (val && val.label) return [val];
            return [];
        },
        formatAnswer() {
            if (!this.answerData || !this.answerData.response) return this.$t('esgPlatform.validation.noAnswer');

            const response = this.answerData.response || {};
            const type = this.getQuestionType();

            // Handle different item types like ValidationData.vue does
            if (this.answerData.type === 'actionPlan') {
                let result = `<span class="font-weight-bold">${response.title || this.$t('esgPlatform.validation.noTitle')}</span>`;
                if (response.files?.length) {
                    const filesHtml = response.files.map(file => this.formatFile(file)).join('<br>');
                    result += `<div>${filesHtml}</div>`;
                }
                return result;
            } else if (this.answerData.type === 'getData') {
                const value = response?.value;
                if (!value && value !== 0) return this.$t('esgPlatform.validation.noData');
                if (typeof value === 'string' || typeof value === 'number') {
                    return value.toString();
                } else if (Array.isArray(value)) {
                    if (value.length && typeof value[0] !== 'object') {
                        return value.join(`<br>`);
                    }
                    return value.map(file => this.formatFile(file)).join('<br>');
                } else if (typeof value === 'object' && value !== null) {
                    if (value.id && value.abbr && 'value' in value) {
                        return `${value.value} ${value.abbr}`;
                    } else if (value.key || value.fileUrl || value.attachmentLink) {
                        return this.formatFile({
                            fileUrl: value.fileUrl || value.attachmentLink,
                            fileName: value.fileName,
                            key: value.key
                        });
                    }
                    return JSON.stringify(value);
                }
                return this.$t('esgPlatform.validation.noData');
            }

            switch (type) {
                case 'text-field':
                case 'text-area':
                case 'numeric':
                    return response.value ?? this.$t('esgPlatform.validation.noResponse');
                case 'radio-button':
                    return ''; // handled by isChoiceType
                case 'check-boxes':
                    return ''; // handled by isChoiceType
                case 'measurement-unit':
                    return response.value ? `${response.value.value || ''} ${response.value.abbr || ''}` : this.$t('esgPlatform.validation.noMeasurement');
                case 'attach-files':
                    if (Array.isArray(response.value)) {
                        return response.value.map(file => this.formatFile(file)).join('<br>');
                    }
                    return this.$t('esgPlatform.validation.noFile');
                case 'digital-signature':
                    if (response.value?.attachmentLink) {
                        return this.isImage(response.value.attachmentLink)
                            ? `<img src="${response.value.attachmentLink}" alt="${this.$t('esgPlatform.digitalSignature')}" style="max-width: 200px;" />`
                            : `<a href="${response.value.attachmentLink}" target="_blank">${this.$t('esgPlatform.validation.viewSignature')}</a>`;
                    }
                    return this.$t('esgPlatform.validation.noSignature');
                case 'get-data':
                    return this.$t('esgPlatform.validation.noData');
                default:
                    return this.$t('esgPlatform.validation.unknownType');
            }
        }
    },
    watch: {
        farmGeofenceDialog(isOpen) {
            if (isOpen) {
                this.$nextTick(() => {
                    this.renderFarmGeofenceMap();
                });
            } else {
                this.teardownFarmGeofenceMap();
            }
        },
        farmLocationDialog(isOpen) {
            if (isOpen) {
                this.$nextTick(() => {
                    this.renderFarmLocationMap();
                });
            } else {
                this.teardownFarmLocationMap();
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close');
        },
        isOptionSelected(option) {
            const val = this.answerData?.response?.value;
            if (!val) return false;
            if (Array.isArray(val)) {
                return val.some(v => v.id === option._id || v.id === option.id || v.label === option.label);
            }
            return val.id === option._id || val.id === option.id || val.label === option.label;
        },
        isFarmerSeparateResponse(respondentType) {
            return respondentType === 'Farmer (Separate response for each farm)' ||
                respondentType === this.$t('esgPlatform.farmerSeparateResponse');
        },
        getDimitraModuleDisplayName(moduleName) {
            if (!moduleName) return '';

            const i18nKey = `esgPlatform.dimitraModuleCatalog.modules.${moduleName}`;
            const translated = this.$t(i18nKey);
            if (translated !== i18nKey) return translated;

            const fallbackNames = {
                Soil: 'Soil Management',
            };

            return fallbackNames[moduleName] || moduleName;
        },
        getTranslatedDimitraFieldLabel(moduleName, key) {
            if (!key) return '';

            const i18nKey = `esgPlatform.dimitraModuleCatalog.fields.${key}`;
            const translated = this.$t(i18nKey);
            if (translated !== i18nKey) return translated;

            return this.formatKey(key, moduleName);
        },
        getChoiceIcon(isSelected) {
            const isRadio = this.getQuestionType() === 'radio-button';
            if (isRadio) {
                return isSelected ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank';
            }
            return isSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline';
        },
        filterRowData(row) {
            const filtered = { ...row };
            delete filtered.id;
            delete filtered.farmId;
            if (filtered.deforestationStatus && typeof filtered.deforestationStatus === 'object') {
                filtered.deforestationStatus = filtered.deforestationStatus.deforestationStatus || '-';
            }
            if (row.farmId && Array.isArray(row.farmId) && !row.farmName) {
                return { farmName: row.farmId[0], ...filtered };
            }
            return filtered;
        },
 
        formatDimitraFieldValue(val) {
            if (Array.isArray(val)) {
                if (!val.length) return '-';
                return val.map(item => this.formatDimitraFieldValue(item)).join(', ');
            }
            if (val === null || val === undefined || val === '') {
                return '-';
            }
            if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
                const d = new Date(val);
                return isNaN(d.getTime())
                    ? val
                    : d.toLocaleDateString(this.getDateLocale(), { day: '2-digit', month: 'long', year: 'numeric' });
            }
            if (typeof val === 'object') {
                return JSON.stringify(val);
            }
            return val;
        },
        isFarmModule(moduleName) {
            return String(moduleName || '').trim().toLowerCase() === 'farms';
        },
        isFarmLocationField(moduleName, key) {
            return this.isFarmModule(moduleName) && String(key || '').trim().toLowerCase() === 'location';
        },
        isFarmGeofenceField(moduleName, key) {
            return this.isFarmModule(moduleName) && String(key || '').trim().toLowerCase() === 'farmgeofence';
        },
        getFarmLocationCoordinateFromValue(location) {
            if (!location || typeof location !== 'object') return null;
            const lat = parseFloat(location?.lat ?? location?.latitude);
            const lng = parseFloat(location?.log ?? location?.lng ?? location?.longitude);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                return { lat, lng };
            }

            return null;
        },
        getFarmGeofenceCoordinates(farmGeofence) {
            const raw = Array.isArray(farmGeofence) ? farmGeofence : [];

            return raw
                .map((coord) => {
                    const lat = parseFloat(coord?.lat ?? coord?.latitude);
                    const lng = parseFloat(coord?.log ?? coord?.lng ?? coord?.longitude);

                    if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        return { lat, lng };
                    }

                    return null;
                })
                .filter(Boolean);
        },
        openFarmLocationDialogWithTitle(title, location) {
            const coordinate = this.getFarmLocationCoordinateFromValue(location);
            if (!coordinate) return;

            this.farmLocationDetails = {
                title: title || this.$t('esgPlatform.validation.farmLocation'),
                coordinate,
            };
            this.farmLocationDialog = true;
        },
        closeFarmLocationDialog() {
            this.farmLocationDialog = false;
        },
        openFarmGeofenceDialogWithTitle(title, farmGeofence) {
            const coordinates = this.getFarmGeofenceCoordinates(farmGeofence);
            if (coordinates.length < 3) return;

            this.farmGeofenceDetails = {
                title: title || this.$t('esgPlatform.validation.farmGeofence'),
                coordinates,
            };
            this.farmGeofenceDialog = true;
        },
        closeFarmGeofenceDialog() {
            this.farmGeofenceDialog = false;
        },
        async renderFarmLocationMap() {
            try {
                const mapContainer = this.$refs.farmLocationMap;
                const coordinate = this.farmLocationDetails?.coordinate;
                if (!mapContainer || !coordinate) return;

                const loader = getGoogleMapsLoader();
                const google = await loader.load();

                this.farmLocationMap = new google.maps.Map(mapContainer, {
                    mapTypeId: 'satellite',
                    center: coordinate,
                    zoom: 17,
                    disableDefaultUI: true,
                    zoomControl: true,
                });

                if (this.farmLocationMarker) {
                    this.farmLocationMarker.setMap(null);
                }

                this.farmLocationMarker = new google.maps.Marker({
                    position: coordinate,
                    map: this.farmLocationMap,
                });
            } catch (error) {
                // ignore map rendering issues and keep dialog usable
            }
        },
        teardownFarmLocationMap() {
            try {
                if (this.farmLocationMarker) {
                    this.farmLocationMarker.setMap(null);
                }
            } catch (error) {
                // ignore cleanup issues
            }

            this.farmLocationMarker = null;
            this.farmLocationMap = null;
        },
        async renderFarmGeofenceMap() {
            try {
                const mapContainer = this.$refs.farmGeofenceMap;
                const coordinates = this.farmGeofenceDetails?.coordinates || [];
                if (!mapContainer || coordinates.length < 3) return;

                const loader = getGoogleMapsLoader();
                const google = await loader.load();

                this.farmGeofenceMap = new google.maps.Map(mapContainer, {
                    mapTypeId: 'satellite',
                    center: coordinates[0],
                    zoom: 15,
                    disableDefaultUI: true,
                    zoomControl: true,
                });

                if (this.farmGeofencePolygon) {
                    this.farmGeofencePolygon.setMap(null);
                }

                this.farmGeofencePolygon = new google.maps.Polygon({
                    paths: coordinates,
                    strokeColor: '#FF6B00',
                    strokeOpacity: 0.85,
                    strokeWeight: 2,
                    fillColor: '#FF6B00',
                    fillOpacity: 0.2,
                    clickable: false,
                    editable: false,
                });
                this.farmGeofencePolygon.setMap(this.farmGeofenceMap);

                const bounds = new google.maps.LatLngBounds();
                coordinates.forEach((coord) => bounds.extend(coord));
                this.farmGeofenceMap.fitBounds(bounds, 60);
            } catch (error) {
                // ignore map rendering issues and keep dialog usable
            }
        },
        teardownFarmGeofenceMap() {
            try {
                if (this.farmGeofencePolygon) {
                    this.farmGeofencePolygon.setMap(null);
                }
            } catch (error) {
                // ignore cleanup issues
            }

            this.farmGeofencePolygon = null;
            this.farmGeofenceMap = null;
        },
        getDateLocale() {
            const localeMap = {
                en: 'en-GB',
                pt: 'pt-PT',
            };

            return localeMap[this.$i18n?.locale] || 'en-GB';
        },
        formatKey(key) {
            return key
                .replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim();
        },
        getQuestionIndex() {
            // Handle both old and new data structures
            if (this.questionData.index) {
                return this.questionData.index;
            } else if (this.questionData.heading?.order && this.questionData.order) {
                return `${this.questionData.heading.order}.${this.questionData.order}`;
            } else if (this.questionData.question?.order) {
                return this.questionData.question.order;
            } else if (this.questionData.order) {
                return this.questionData.order;
            }
            return '';
        },
        getQuestionTitle() {
            // Handle both old and new data structures
            if (this.questionData.question?.title) {
                return this.questionData.question.title;
            } else if (this.questionData.title) {
                return this.questionData.title;
            }
            return '';
        },
        getQuestionDescription() {
            // Handle both old and new data structures
            if (this.questionData.question?.description) {
                return this.questionData.question.description;
            } else if (this.questionData.description) {
                return this.questionData.description;
            }
            return '';
        },
        getQuestionType() {
            // Handle both old and new data structures
            if (this.questionData.question?.questionType) {
                return this.questionData.question.questionType;
            } else if (this.questionData.questionType) {
                return this.questionData.questionType;
            }
            return '';
        },
        getQuestionScore() {
            // Handle both old and new data structures
            if (this.questionData.question?.score) {
                return this.questionData.question.score;
            } else if (this.questionData.score) {
                return this.questionData.score;
            }
            return null;
        },
        getIssueTypeColor(type) {
            const colorMap = {
                'Environmental': 'green',
                'Social': 'blue',
                'Governance': 'purple',
                'Economic': 'orange'
            };
            return colorMap[type] || 'grey';
        },
        formatFile(file) {
            // Handle different file object structures
            const url = file.fileUrl || file.url || `https://dimitra-pre-prod-public-images.s3.amazonaws.com/${file.key}`;
            const name = file.fileName || file.name || file.key || this.$t('esgPlatform.validation.unnamedFile');
            return this.isImage(name)
                ? `<img src="${url}" alt="${name}" style="max-width: 200px;" />`
                : `<a href="${url}" target="_blank">${name}</a>`;
        },
        isImage(fileString) {
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
            const path = (fileString || '').split('?')[0];
            const ext = path.slice(path.lastIndexOf('.')).toLowerCase();
            return imageExtensions.includes(ext);
        }
    }
}
</script>

<style scoped>
.v-card-title {
    padding: 16px 24px;
}

.context-info {
    padding: 8px 0;
}

.context-info .text-subtitle-2 {
    color: #117443;
}

.context-info .text-caption {
    color: #666;
    font-style: italic;
}

.v-expansion-panel-header {
    padding: 12px 16px;
}

.v-expansion-panel-content {
    padding: 0 16px 16px;
}

.question-card {
    background-color: #f5f5f5;
    border-radius: 8px;
    padding: 20px 24px;
}

.question-title {
    font-size: 1rem;
    line-height: 1.5;
    color: #111827;
}

.option-row {
    margin-bottom: 10px;
}

.option-selected {
    font-weight: 500;
    color: #111827;
}

.option-unselected {
    color: #4B5563;
}

.dialog-header {
    border-bottom: 1px solid #edf1ee;
}

.map-link-button {
    padding: 0;
    border: none;
    background: transparent;
    color: #117443;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    text-decoration: underline;
}

.farm-map {
    width: 100%;
    height: 70vh;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid #edf1ee;
}
</style>
